import type { ProfileResponse } from '../types/api'
import { isoToday, isoYearsAgo, parseIsoDate } from './date'

export const ADULT_AGE = 18

/**
 * Selectable range for a date of birth. The upper bound matters: an unbounded field accepts a
 * future date, which makes {@link ageFromDob} negative, and a negative age reads as a minor -
 * so the form would demand a guardian for someone not born yet.
 */
export const DOB_MIN = isoYearsAgo(100)
export const DOB_MAX = isoToday()
/** Where the calendar opens before anything is picked - a plausible learner birth year. */
export const DOB_OPEN_AT = isoYearsAgo(15)

/** Whole years between a yyyy-MM-dd date of birth and today, or null if unparseable. */
export function ageFromDob(dob: string | undefined | null): number | null {
  if (!dob) return null
  const birth = parseIsoDate(dob)
  if (birth === null) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const monthDiff = now.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age--
  return age
}

/**
 * Whether this participant still needs a linked parent or guardian.
 *
 * Date of birth wins over the stored is_minor flag: that flag is written once at registration and
 * never recalculated, so a learner who signed up at 17 would otherwise be treated as a minor for
 * life. The flag is only the fallback for accounts that never gave a date of birth.
 */
export function requiresGuardian(profile: Pick<ProfileResponse, 'dob' | 'isMinor'>): boolean {
  const age = ageFromDob(profile.dob)
  if (age !== null) return age < ADULT_AGE
  return profile.isMinor
}
